import { Injectable } from "@angular/core";
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
  UrlTree,
  CanActivateChild,
} from "@angular/router";
import { Observable, of } from "rxjs";
import { catchError, map, switchMap, take } from "rxjs/operators";
import { JwtAuthService } from "../services/auth/jwt-auth.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { AppConfirmService } from "../services/app-confirm/app-confirm.service";
import { BlackListService } from "../services/blacklist.service";
import { BlackList } from "../interfaces/blacklist";
import { FilialService } from "../services/filial.service";

@Injectable({ providedIn: "root" })
export class UserRoleGuard implements CanActivate, CanActivateChild {
  private _notified = new Set<string>();
  private _lastUserName: string | null = null;

  constructor(
    private router: Router,
    private jwtAuth: JwtAuthService,
    private blackListService: BlackListService,
    private filialService: FilialService,
    private snack: MatSnackBar,
    private appConfirmService: AppConfirmService
  ) {
    // limpa notificações quando o usuário muda (ex.: logout/login)
    this.jwtAuth.item$.pipe(take(1)).subscribe((item) => {
      this._lastUserName = item?.userName ?? null;
    });

    // observa mudanças de usuário e reseta notificações ao trocar de conta
    this.jwtAuth.item$.subscribe((item) => {
      const userName = item?.userName ?? null;
      if (this._lastUserName && this._lastUserName !== userName) {
        this._notified.clear();
      }
      this._lastUserName = userName;
    });
  }

  private _notifyKey(filialId: string, tipo: string) {
    return `${filialId}:${tipo}`;
  }

  // canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
  // var user = this.jwtAuth.getUser();
  // var roles = this.jwtAuth.item$.value.roles;
  // console.log('ROLES USER:' + roles);
  // console.log('ROLES ROUTE:' + route.data.roles);

  // if (
  //   user &&
  //   route.data &&
  //   route.data.roles &&
  //   route.data.roles.some(r => roles.includes(r))
  // ) {

  //   return true;
  // } else {
  //   this.appConfirmService.confirm({title: 'A T E N Ç Ã O !', message:
  //   'Não Autorizado!Você não tem autorização p/ fazer esta operação, contate o Administrador!', error: true})
  // console.log('Não Autorizado!Você não tem autorização p/ fazer esta operação, contate o Administrador!');
  //   return false;
  // }
  // }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.canActivate(route, state);
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    // 1) verifica roles - se não autorizado, bloqueia imediatamente
    const user = this.jwtAuth.getUser();
    const roles: string[] = this.jwtAuth.item$?.value?.roles ?? [];
    const requiredRoles: string[] | undefined = route.data?.roles;

    if (
      requiredRoles &&
      !(user && requiredRoles.some((r) => roles.includes(r)))
    ) {
      this.appConfirmService
        .confirm({
          title: "A T E N Ç Ã O !",
          message:
            "Não Autorizado! Você não tem autorização p/ fazer esta operação, contate o Administrador!",
          error: true,
        })
        .subscribe();
      return of(false);
    }

    console.log("UserRoleGuard: autorizado por roles, verificações adicionais..." + user.usuario);

    // 2) se autorizado por roles, executa as verificações adicionais (blacklist, vencimento, títulos)
    return this.jwtAuth.itemRemoto$.pipe(
      take(1),
      switchMap((itemRemoto) => {
        if (!itemRemoto) return of(true);

        const filialCodigo = (itemRemoto.filialCodigo ?? "")
          .toString()
          .replace(/\D/g, "");
        const filialId = itemRemoto.filialId ?? null;
        const item = this.jwtAuth.item$?.value ?? null;
        const tokenBase = item?.token ?? null;
        const userName = item?.userName ?? null;

        console.log("UserRoleGuard: autorizado por roles, verificações adicionais FILIAL_ID...");

        if (!filialId) {
          this.appConfirmService
            .confirm({
              title: "Filial não encontrada",
              message: "Não foi possível determinar a filial associada ao seu usuário. Contate o administrador.",
              error: true,
            })
            .subscribe();
          return of(this.router.parseUrl("/sessions/signin"));
        }

        console.log("UserRoleGuard: autorizado por roles, verificações adicionais ACESSO BLACKLIST BOLQUEIO E FINANCEIRO...");

        return this.filialService.getFilial(Number(filialId)).pipe(
          take(1),
          switchMap((filial) => {
            const nome = `${filial?.razaoSocial ?? ""} - ${filial?.nomeFantasia ?? ""}`;
            const payload: Partial<BlackList> = {
              codigo: filialCodigo,
              desativado: false,
              nome,
              ultimoLoginUserName: userName,
            };

            return this.blackListService
              .saveOrUpdateLog(payload as BlackList, tokenBase)
              .pipe(
                take(1),
                switchMap((result: any) => {
                  // bloqueio por blacklist -> mostra mensagem e redireciona para login
                  if (result?.onBlackList) {
                    this.appConfirmService
                      .confirm({
                        title: "Acesso Bloqueado ou Licença Expirada",
                        message:
                          "Seu acesso ao Infokaw está bloqueado ou a licença expirou. Para continuar utilizando nossos serviços, regularize sua situação entrando em contato com o suporte. Estamos prontos para ajudar e esclarecer qualquer dúvida.",
                        error: true,
                        icon: "report_problem",
                      })
                      .subscribe();
                    return of(this.router.parseUrl("/sessions/signin"));
                  }

                  // aviso de vencimento próximo (não bloqueante)
                  if (result?.vencimento) {
                    const dias = this._diasRestantes(result.vencimento);
                    const key = this._notifyKey(String(filialId), "vencimento");
                    if (dias <= 5 && !this._notified.has(key)) {
                      this._notified.add(key);
                      this.appConfirmService
                        .confirm({
                          title: "Atenção: Licença Próxima do Vencimento",
                          message: `Olá! Seu acesso ao Infokaw irá expirar/bloquear em ${dias} dia(s). Para garantir a continuidade dos seus serviços, por favor, regularize sua situação o quanto antes. Em caso de dúvidas ou necessidade de suporte, nossa equipe está à disposição para ajudar. Agradecemos pela parceria!`,
                          error: true,
                          icon: "report_problem",
                        })
                        .subscribe();
                    }
                  }

                  // títulos vencidos -> alerta para admUsers (não bloqueante)
                  return this.blackListService.getVencidos(filialCodigo).pipe(
                    take(1),
                    map((titulos: any[]) => {
                      //montar um lista de titulos com valores, saldo e data vencimento
                      console.log("Títulos vencidos", titulos);
                      const titulosVencidos = titulos.map((titulo) => ({
                        valorSaldo: titulo.valorSaldo,
                        vcto: titulo.vcto,
                      }));
                      
                      console.log("Títulos vencidos mapeados", titulosVencidos);
                      console.log(titulosVencidos.length);

                      if (titulosVencidos && titulosVencidos.length > 0) {
                        const admUsers = (result.admUser as string)
                          .split(",")
                          .map((u: string) => u.trim().toLowerCase());
                        if (admUsers.includes((userName ?? "").toLowerCase())) {
                          const key = this._notifyKey(
                            String(filialId),
                            "titulos"
                          );
                          if (!this._notified.has(key)) {
                            this._notified.add(key);
                            this.appConfirmService
                              .confirm({
                                title: "** C O M U N I C A D O **",
                                message:
                                  "Olá! Identificamos que existem títulos vencidos há mais de 5 dias para a empresa. Por favor, quando possível, regularize sua situação para evitar eventuais transtornos. Caso precise de qualquer auxílio, nossa equipe está à disposição para ajudar. Muito obrigado!",
                                error: true,
                                icon: "report_problem",
                              })
                              .subscribe();
                          }
                        }
                        // Se o usuário NÃO for root, redireciona para login
                        if ((user?.usuario ?? "").toLowerCase() !== "root") {
                            const totalSaldo = titulosVencidos.reduce(
                            (sum, t) => sum + (Number(t.valorSaldo) || 0),
                            0
                            );
                            const titulosList = titulosVencidos
                              .map((t, idx) => {
                              const vctoDate = new Date(t.vcto);
                              const vctoFormatted = vctoDate.toLocaleDateString("pt-BR");
                              return `#${idx + 1}: Vencimento: ${vctoFormatted}, Valor: R$ ${Number(t.valorSaldo).toFixed(2)}`;
                              })
                              .join('\n');
                            this.appConfirmService
                              .confirm({
                              title: "Acesso Bloqueado por Títulos Vencidos",
                              message:
                                `Seu acesso ao Infokaw foi bloqueado automaticamente devido à existência de títulos vencidos há 5 dias ou mais em seu cadastro.\n\n` +
                                `Títulos vencidos:\n${titulosList}\n\n` +
                                `Valor total em aberto: R$ ${totalSaldo.toFixed(2)}.\n\n` +
                                `Para restabelecer o acesso, regularize sua situação financeira. Em caso de dúvidas ou necessidade de suporte, nossa equipe está à disposição para ajudar. Agradecemos pela compreensão.`,
                              error: true,
                              icon: "report_problem",
                              })
                              .subscribe();
                          return this.router.parseUrl("/sessions/signin");
                        }
                      }

                      

                      return true;
                    }),
                    catchError((err) => {
                      console.error("guard getVencidos erro", err);
                      return of(true);
                    })
                  );
                }),
                catchError((err) => {
                  console.error("guard saveOrUpdateLog erro", err);
                  return of(true);
                })
              );
          }),
          catchError((err) => {
            console.error("guard getFilial erro", err);
            return of(true);
          })
        );
      }),
      catchError((err) => {
        console.error("UserRoleGuard erro geral", err);
        return of(true);
      })
    );
  }

  private _diasRestantes(vencimentoIsoOrDate: string | Date): number {
    const oneDay = 1000 * 60 * 60 * 24;
    const end = new Date(vencimentoIsoOrDate);
    const today = new Date();
    end.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return Math.round((end.getTime() - today.getTime()) / oneDay);
  }
}